import type {
    AreaServedInput,
    BookSchemaInput,
    BreadcrumbItemInput,
    ContentItemSchemaInput,
    FAQItemInput,
    JsonLdObject,
    JsonLdValue,
    ProductSchemaInput,
    ReviewSchemaInput,
    SeoEntityIds,
    SeoMeta,
    ServiceSchemaInput,
    SiteSeoProfile,
    VehicleInput,
    VehicleServiceInput,
    VideoObjectInput,
} from './types';

export const trimSlashes = (value: string) => value.replace(/^\/+|\/+$/g, '');

export const toAbsoluteUrl = (value: string | undefined, siteUrl: string) => {
    if (!value) return undefined;
    if (/^https?:\/\//i.test(value)) return value;

    const base = siteUrl.replace(/\/+$/g, '');
    const path = value.startsWith('/') ? value : `/${value}`;

    return `${base}${path}`;
};

export const getImageType = (value?: string) => {
    if (!value) return undefined;
    if (/\.jpe?g($|\?)/i.test(value)) return 'image/jpeg';
    if (/\.png($|\?)/i.test(value)) return 'image/png';
    if (/\.webp($|\?)/i.test(value)) return 'image/webp';
    if (/\.gif($|\?)/i.test(value)) return 'image/gif';
    if (/\.svg($|\?)/i.test(value)) return 'image/svg+xml';

    return undefined;
};

export const toAustralianPhone = (phone?: string) => {
    if (!phone) return undefined;

    const digits = phone.replace(/\D/g, '');
    if (!digits) return undefined;
    if (digits.startsWith('61')) return `+${digits}`;
    if (digits.startsWith('0')) return `+61${digits.slice(1)}`;

    return phone;
};

const isEmptyJsonLdObject = (value: JsonLdValue) =>
    Boolean(value) && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0;

const compactArray = (value: JsonLdValue[]) =>
    value
        .map((item) => {
            if (Array.isArray(item)) return compactArray(item);
            if (item && typeof item === 'object') return compactObject(item as JsonLdObject);
            return item;
        })
        .filter((item) => {
            if (item === undefined || item === null || item === '') return false;
            if (Array.isArray(item)) return item.length > 0;
            if (isEmptyJsonLdObject(item)) return false;
            return true;
        });

export const compactObject = <T extends JsonLdObject>(value: T): T =>
    Object.fromEntries(
        Object.entries(value)
            .map(([key, item]) => {
                if (Array.isArray(item)) return [key, compactArray(item)];
                if (item && typeof item === 'object') return [key, compactObject(item as JsonLdObject)];
                return [key, item];
            })
            .filter(([, item]) => {
                if (item === undefined || item === null || item === '') return false;
                if (Array.isArray(item)) return item.length > 0;
                if (isEmptyJsonLdObject(item as JsonLdValue)) return false;
                return true;
            }),
    ) as T;

export const stringifyJsonLd = (schema: JsonLdObject | JsonLdObject[]) =>
    JSON.stringify(schema).replace(/</g, '\\u003c');

export const getSeoEntityIds = (profile: SiteSeoProfile): SeoEntityIds => {
    const base = profile.siteUrl.replace(/\/+$/g, '');

    return {
        organization: profile.ids?.organization || `${base}/#organization`,
        localBusiness: profile.ids?.localBusiness || `${base}/#localbusiness`,
        website: profile.ids?.website || `${base}/#website`,
    };
};

export const normaliseSeoMeta = (meta: SeoMeta | undefined, profile: SiteSeoProfile): SeoMeta => ({
    ...profile.defaultMeta,
    ...(meta || {}),
});

export const buildSameAs = (profile: SiteSeoProfile) =>
    [
        ...(profile.organization.sameAs || []),
        ...(profile.localBusiness?.sameAs || []),
    ].filter((item): item is string => Boolean(item && typeof item === 'string'));

const resolveType = (value: string | string[] | undefined, fallback: string | string[]) => value || fallback;

const buildAddressSchema = (profile: SiteSeoProfile) => {
    const address = profile.localBusiness?.address;
    if (!address) return undefined;

    return compactObject({
        '@type': 'PostalAddress',
        streetAddress: address.streetAddress,
        addressLocality: address.addressLocality,
        addressRegion: address.addressRegion,
        postalCode: address.postalCode,
        addressCountry: address.addressCountry,
    });
};

const buildGeoSchema = (profile: SiteSeoProfile) => {
    const geo = profile.localBusiness?.geo;
    if (!geo) return undefined;

    return compactObject({
        '@type': 'GeoCoordinates',
        latitude: Number(geo.latitude.toFixed(5)),
        longitude: Number(geo.longitude.toFixed(5)),
    });
};

export const buildAreaServedSchema = (areas: AreaServedInput[] = []) =>
    areas.map((area) => {
        if (typeof area === 'string') {
            return compactObject({
                '@type': 'Place',
                name: area,
            });
        }

        return compactObject({
            '@type': area.type || 'Place',
            name: area.name,
        });
    });

export const buildServiceAreaSchema = (profile: SiteSeoProfile) => {
    const geo = profile.localBusiness?.geo;
    const radius = profile.localBusiness?.serviceAreaRadius;

    if (!geo || !radius) return undefined;

    return compactObject({
        '@type': 'GeoCircle',
        geoMidpoint: buildGeoSchema(profile),
        geoRadius: radius,
    });
};

const buildContactPointSchema = (profile: SiteSeoProfile) => {
    const contactPoint = profile.localBusiness?.contactPoint;
    const phone = toAustralianPhone(contactPoint?.telephone || profile.localBusiness?.telephone);
    const email = contactPoint?.email || profile.localBusiness?.email;

    if (!contactPoint && !phone && !email) return undefined;

    return compactObject({
        '@type': 'ContactPoint',
        telephone: phone,
        email,
        contactType: contactPoint?.contactType || 'customer service',
        areaServed: contactPoint?.areaServed || 'AU',
        availableLanguage: contactPoint?.availableLanguage || profile.language || 'en-AU',
    });
};

export const buildOrganizationSchema = (profile: SiteSeoProfile) => {
    const ids = getSeoEntityIds(profile);

    return compactObject({
        '@type': resolveType(profile.organization.schemaType, 'Organization'),
        '@id': ids.organization,
        name: profile.organization.name,
        legalName: profile.organization.legalName,
        alternateName: profile.organization.alternateName,
        url: profile.organization.url || profile.siteUrl,
        logo: toAbsoluteUrl(profile.organization.logo, profile.siteUrl),
        image: Array.isArray(profile.organization.image)
            ? profile.organization.image.map((image) => toAbsoluteUrl(image, profile.siteUrl))
            : toAbsoluteUrl(profile.organization.image, profile.siteUrl),
        sameAs: buildSameAs(profile),
    });
};

export const buildWebsiteSchema = (profile: SiteSeoProfile) => {
    const ids = getSeoEntityIds(profile);

    return compactObject({
        '@type': 'WebSite',
        '@id': ids.website,
        name: profile.siteName,
        url: profile.siteUrl,
        publisher: {
            '@id': ids.organization,
        },
        inLanguage: profile.language || 'en-AU',
    });
};

export const buildLocalBusinessSchema = (profile: SiteSeoProfile) => {
    const ids = getSeoEntityIds(profile);
    const localBusiness = profile.localBusiness;

    if (!localBusiness) return undefined;

    const taxId = localBusiness.taxId;

    return compactObject({
        '@type': resolveType(localBusiness.schemaType, ['LocalBusiness', 'ProfessionalService']),
        '@id': ids.localBusiness,
        name: localBusiness.name || profile.organization.name,
        legalName: localBusiness.legalName || profile.organization.legalName,
        description: localBusiness.description || profile.defaultMeta.description,
        url: localBusiness.url || profile.siteUrl,
        image: compactArray(
            (Array.isArray(localBusiness.image) ? localBusiness.image : [localBusiness.image, profile.defaultImage, profile.defaultMeta.ogImage])
                .map((image) => toAbsoluteUrl(image, profile.siteUrl)),
        ),
        logo: toAbsoluteUrl(localBusiness.logo || profile.organization.logo, profile.siteUrl),
        telephone: toAustralianPhone(localBusiness.telephone),
        email: localBusiness.email,
        taxID: taxId ? `${localBusiness.taxIdLabel || 'ABN'} ${taxId}` : undefined,
        identifier: taxId
            ? {
                  '@type': 'PropertyValue',
                  propertyID: localBusiness.taxIdLabel || 'ABN',
                  value: taxId,
              }
            : undefined,
        address: buildAddressSchema(profile),
        geo: buildGeoSchema(profile),
        areaServed: buildAreaServedSchema(localBusiness.areaServed),
        currenciesAccepted: localBusiness.currenciesAccepted,
        paymentAccepted: localBusiness.paymentAccepted,
        priceRange: localBusiness.priceRange,
        contactPoint: buildContactPointSchema(profile),
        knowsAbout: localBusiness.knowsAbout,
        sameAs: buildSameAs(profile),
        parentOrganization: {
            '@id': ids.organization,
        },
    });
};

export const buildBusinessGraph = (profile: SiteSeoProfile) =>
    compactObject({
        '@context': 'https://schema.org',
        '@graph': compactArray([
            buildOrganizationSchema(profile),
            profile.includeWebsiteSchema ? buildWebsiteSchema(profile) : undefined,
            buildLocalBusinessSchema(profile),
        ]),
    });

export const buildWebPageSchema = (meta: SeoMeta | undefined, profile: SiteSeoProfile) => {
    const ids = getSeoEntityIds(profile);
    const pageMeta = normaliseSeoMeta(meta, profile);
    const pageUrl = toAbsoluteUrl(pageMeta.url, profile.siteUrl);
    const pageImage = toAbsoluteUrl(pageMeta.ogImage || profile.defaultImage, profile.siteUrl);

    return compactObject({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': pageUrl ? `${pageUrl}#webpage` : undefined,
        name: pageMeta.title,
        description: pageMeta.description,
        url: pageUrl,
        image: pageImage,
        isPartOf: {
            '@id': ids.organization,
        },
        publisher: {
            '@id': ids.organization,
        },
        about: profile.localBusiness
            ? {
                  '@id': ids.localBusiness,
              }
            : undefined,
        inLanguage: profile.language || 'en-AU',
    });
};

export const buildServiceSchema = (input: ServiceSchemaInput, profile: SiteSeoProfile) => {
    const ids = getSeoEntityIds(profile);
    const serviceUrl = toAbsoluteUrl(input.url, profile.siteUrl);
    const serviceImage = toAbsoluteUrl(input.image || profile.defaultMeta.ogImage, profile.siteUrl);

    return compactObject({
        '@context': 'https://schema.org',
        '@graph': compactArray([
            buildOrganizationSchema(profile),
            buildLocalBusinessSchema(profile),
            {
                '@type': 'Service',
                '@id': serviceUrl ? `${serviceUrl}#service` : undefined,
                name: input.name,
                description: input.description,
                serviceType: input.serviceType || input.name,
                url: serviceUrl,
                image: serviceImage,
                provider: {
                    '@id': input.providerId || ids.localBusiness,
                },
                areaServed: input.areaServed || buildServiceAreaSchema(profile) || buildAreaServedSchema(profile.localBusiness?.areaServed),
                offers: input.offers,
                hasOfferCatalog: input.hasOfferCatalog,
                additionalProperty: input.additionalProperty,
                availableChannel:
                    input.availableChannel ||
                    compactObject({
                        '@type': 'ServiceChannel',
                        serviceUrl,
                        servicePhone: profile.localBusiness?.telephone
                            ? {
                                  '@type': 'ContactPoint',
                                  telephone: toAustralianPhone(profile.localBusiness.telephone),
                              }
                            : undefined,
                    }),
                mainEntityOfPage: input.mainEntityOfPage || serviceUrl,
            },
        ]),
    });
};

export const buildVehicleServiceSchema = (input: VehicleServiceInput, profile: SiteSeoProfile) =>
    buildServiceSchema(
        {
            ...input,
            name: input.name,
            serviceType: input.serviceType || input.vehicleStyle || input.name,
            additionalProperty: compactArray([
                ...(input.additionalProperty || []),
                input.passengerCapacity
                    ? {
                          '@type': 'PropertyValue',
                          name: 'Passenger capacity',
                          value: input.passengerCapacity,
                      }
                    : undefined,
                input.vehicleStyle
                    ? {
                          '@type': 'PropertyValue',
                          name: 'Vehicle style',
                          value: input.vehicleStyle,
                      }
                    : undefined,
            ]) as JsonLdObject[],
        },
        profile,
    );

export const buildContentItemSchema = (input: ContentItemSchemaInput, profile: SiteSeoProfile) => {
    const ids = getSeoEntityIds(profile);
    const itemUrl = toAbsoluteUrl(input.url, profile.siteUrl);
    const itemImage = toAbsoluteUrl(input.image || profile.defaultMeta.ogImage, profile.siteUrl);

    return compactObject({
        '@context': 'https://schema.org',
        '@graph': compactArray([
            buildOrganizationSchema(profile),
            buildLocalBusinessSchema(profile),
            {
                '@type': input.schemaType,
                '@id': itemUrl ? `${itemUrl}#${input.schemaType.toLowerCase()}` : undefined,
                headline: input.schemaType === 'CreativeWork' ? undefined : input.headline || input.title,
                name: input.title,
                description: input.description,
                image: itemImage,
                datePublished: input.datePublished,
                dateModified: input.dateModified || input.datePublished,
                author: input.authorId
                    ? {
                          '@id': input.authorId,
                      }
                    : {
                          '@type': 'Organization',
                          '@id': ids.organization,
                          name: input.authorName || profile.organization.name,
                          url: profile.siteUrl,
                      },
                publisher: {
                    '@id': ids.organization,
                },
                mainEntityOfPage: itemUrl,
                inLanguage: input.inLanguage || profile.language || 'en-AU',
            },
        ]),
    });
};

export const buildArticleSchema = (input: Omit<ContentItemSchemaInput, 'schemaType'>, profile: SiteSeoProfile) =>
    buildContentItemSchema({ ...input, schemaType: 'Article' }, profile);

export const buildBlogPostingSchema = (input: Omit<ContentItemSchemaInput, 'schemaType'>, profile: SiteSeoProfile) =>
    buildContentItemSchema({ ...input, schemaType: 'BlogPosting' }, profile);

export const buildCreativeWorkSchema = (input: Omit<ContentItemSchemaInput, 'schemaType'>, profile: SiteSeoProfile) =>
    buildContentItemSchema({ ...input, schemaType: 'CreativeWork' }, profile);

export const buildBreadcrumbSchema = (items: BreadcrumbItemInput[], profile: SiteSeoProfile) =>
    compactObject({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((item, index) =>
            compactObject({
                '@type': 'ListItem',
                position: index + 1,
                name: item.name,
                item: toAbsoluteUrl(item.url, profile.siteUrl),
            }),
        ),
    });

export const buildFAQPageSchema = (items: FAQItemInput[]) => {
    const mainEntity = items
        .filter((item) => item.question && item.answer)
        .map((item) =>
            compactObject({
                '@type': 'Question',
                name: item.question,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: item.answer,
                },
            }),
        );

    if (!mainEntity.length) return undefined;

    return compactObject({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity,
    });
};

export const toIsoDuration = (seconds?: number) => {
    if (!seconds) return undefined;

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    return `PT${hours ? `${hours}H` : ''}${minutes ? `${minutes}M` : ''}${
        remainingSeconds || (!hours && !minutes) ? `${remainingSeconds}S` : ''
    }`;
};

export const buildVideoObjectSchema = (input: VideoObjectInput, profile: SiteSeoProfile) => {
    const ids = getSeoEntityIds(profile);

    return compactObject({
        '@context': 'https://schema.org',
        '@type': 'VideoObject',
        name: input.name,
        description: input.description,
        thumbnailUrl: Array.isArray(input.thumbnailUrl)
            ? input.thumbnailUrl.map((image) => toAbsoluteUrl(image, profile.siteUrl))
            : toAbsoluteUrl(input.thumbnailUrl, profile.siteUrl),
        uploadDate: input.uploadDate,
        duration: input.duration,
        embedUrl: input.embedUrl,
        url: toAbsoluteUrl(input.url, profile.siteUrl),
        publisher: {
            '@id': input.publisherId || ids.organization,
        },
    });
};

export const buildVideoObjectGraph = (inputs: VideoObjectInput[], profile: SiteSeoProfile) =>
    compactObject({
        '@context': 'https://schema.org',
        '@graph': inputs.map((input) => {
            const schema = buildVideoObjectSchema(input, profile);
            const { '@context': _context, ...video } = schema;
            return video;
        }),
    });

export const buildVehicleSchema = (input: VehicleInput, profile: SiteSeoProfile) =>
    compactObject({
        '@context': 'https://schema.org',
        '@type': 'Vehicle',
        name: input.name,
        description: input.description,
        url: toAbsoluteUrl(input.url, profile.siteUrl),
        image: Array.isArray(input.image)
            ? input.image.map((image) => toAbsoluteUrl(image, profile.siteUrl))
            : toAbsoluteUrl(input.image, profile.siteUrl),
        brand: input.brand,
        model: input.model,
        vehicleModelDate: input.vehicleModelDate,
        bodyType: input.bodyType,
        color: input.color,
        vehicleSeatingCapacity: input.vehicleSeatingCapacity,
        additionalProperty: input.additionalProperty,
    });

export const buildReviewSchema = (input: ReviewSchemaInput, profile: SiteSeoProfile) => {
    const ids = getSeoEntityIds(profile);

    return compactObject({
        '@context': 'https://schema.org',
        '@type': 'Review',
        name: input.name,
        description: input.description,
        url: toAbsoluteUrl(input.url, profile.siteUrl),
        image: toAbsoluteUrl(input.image, profile.siteUrl),
        author: {
            '@id': input.authorId || ids.organization,
        },
        publisher: {
            '@id': input.publisherId || ids.organization,
        },
        itemReviewed: input.itemReviewed,
        reviewBody: input.reviewBody || input.description,
    });
};

export const buildProductSchema = (input: ProductSchemaInput, profile: SiteSeoProfile) =>
    compactObject({
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: input.name,
        description: input.description,
        url: toAbsoluteUrl(input.url, profile.siteUrl),
        image: Array.isArray(input.image)
            ? input.image.map((image) => toAbsoluteUrl(image, profile.siteUrl))
            : toAbsoluteUrl(input.image, profile.siteUrl),
        brand: typeof input.brand === 'string' ? { '@type': 'Brand', name: input.brand } : undefined,
        sku: input.sku,
        category: input.category,
        offers: input.offers,
    });

export const buildBookSchema = (input: BookSchemaInput, profile: SiteSeoProfile) =>
    compactObject({
        ...buildProductSchema(input, profile),
        '@type': 'Book',
        author: typeof input.author === 'string' ? { '@type': 'Person', name: input.author } : input.author,
        bookFormat: input.bookFormat,
        isbn: input.isbn,
    });
